import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageBubble } from './MessageBubble';
import { ModelViewEditGroup } from './ModelViewEditGroup';
import { SecurityNotice } from './SecurityNotice';
import { ToolResultRenderer } from './tool-results';
import { ReasoningPill } from './ReasoningPill';
import { PlanChecklist } from './PlanChecklist';
import { SummaryCard } from './SummaryCard';
import {
  groupSystemEdits,
  isModelViewEditGroup,
  TABLE_EDIT_PREFIX_RE,
} from './chat-thread-grouping';
import { TableEditReceipt } from './TableEditReceipt';
import type {
  ToolName,
  SummarySection,
  ProvenanceRecord,
  ModelSchema,
  TableInputDefinition,
  AliasMatchState,
} from '@arcanum-hq/shared-types';
import type {
  ToolCallState,
  ThinkingBlock,
  PlanState,
} from '@/hooks/use-streaming-v2';
import type { MarketFieldValueUnit } from '@/hooks/use-default-match-suggestions';

/**
 * Tools whose `tool_result` cards are hidden in the receipt-grouping iteration:
 *
 * - `market_data_enrichment` — narrated in agent prose; raw card is noise.
 * - `set_table_inputs` — agent narrates; Phase 5 ships `<TableFillProposal>` and
 *   revisits this entry then.
 * - `propose_plan` — synthetic; surfaced as `<PlanChecklist>` not a card.
 *
 * Mirrors the legacy `HIDDEN_TOOL_RESULTS` set for `set_table_inputs` +
 * `market_data_enrichment`; adds `propose_plan` for the new receipt grouping.
 */
const HIDDEN_FROM_RECEIPT_GROUPING = new Set<ToolName>([
  'propose_plan',
  'market_data_enrichment',
  'set_table_inputs',
]);

export interface ChatMessage {
  id: string;
  /**
   * `'system'` was added 2026-04-26 by in-platform-model-view Phase 4
   * Session 4. The `/model-edit` endpoint appends `[model-view:${namedRange}]`
   * SystemMessages to the LangGraph thread; the API's `getMessages` was
   * relaxed to surface them, and `<ChatThread>` renders adjacent same-
   * namedRange entries via `<ModelViewEditGroup>` (collapse within 30s).
   */
  role: 'human' | 'ai' | 'system';
  content: string;
  toolResults?: Array<{ tool: ToolName; result: unknown }>;
  /**
   * Structured tool failures rendered inline as `<ToolErrorCallout>` blocks
   * after the toolResults section. Sourced from either the live SSE stream
   * (use-streaming-v2) or the hydration pipeline (use-conversations-v2).
   */
  toolErrors?: Array<{
    tool: ToolName;
    code: string;
    message: string;
    details?: Record<string, unknown>;
  }>;
  /**
   * Per-message security redaction events (Phase 5 Session 2). Each entry
   * maps to one `<SecurityNotice variant="redact">` chip. Stage 1 (token)
   * indicates a deterministic per-token regex caught + substituted the span
   * before it shipped; Stage 2 (semantic) indicates a post-stream Haiku
   * check caught a methodology / calibration / semantic-rule disclosure.
   */
  redactedSpans?: Array<{
    category:
      | 'system_prompt'
      | 'tool_name'
      | 'cross_user_id'
      | 'methodology'
      | 'calibration_band'
      | 'semantic_rule';
    stage: 'token' | 'semantic';
    redactedSpan: string;
    replacement: string;
  }>;
  activeToolCall?: ToolName | null;
  isStreaming?: boolean;
  /**
   * ISO-8601 timestamp from the upstream LangGraph checkpoint (via the
   * conversation-detail GET response). Used by `<ChatThread>`'s render-time
   * grouping to enforce the 30s window invariant. Optional — older
   * messages and live-streamed messages may omit it.
   */
  created_at?: string;
  /**
   * Frozen point-in-time table values for a `[table-edit:*]` breadcrumb —
   * `namedRange → { value }` as applied by THAT edit. Surfaced from the
   * SystemMessage's `additional_kwargs.tableSnapshot` so `<TableEditReceipt>`
   * renders the historical table state instead of the live model. Absent on
   * legacy receipts (pre-2026-06-09) + non-table-edit messages.
   */
  tableSnapshot?: Record<string, { value: unknown; source?: string }>;
  // Phase 2 Session 4 — meta UI fields (also surfaced by hydration via
  // use-conversations-v2's deriveToolCallStates).
  toolCallStates?: Map<string, ToolCallState>;
  thinking?: ThinkingBlock | null;
  plan?: PlanState | null;
  summary?: { sections: SummarySection[] } | null;
}

interface ChatThreadProps {
  messages: ChatMessage[];
  initials?: string;
  /**
   * Phase 5 Session 2 (2026-05-03) — terminating security block event.
   */
  securityBlock?: { reason: string } | null;
  /**
   * Phase 2 Session 5 — refined `agentState.inputProvenance` (Zod-parsed at
   * the route boundary in `routes/builder.tsx`). Threaded through to
   * `<MessageBubble>` for inline `<ProvenanceChip>` rendering. `null` while
   * `useConversation` loads or on schema-shape drift.
   */
  inputProvenance?: Record<string, ProvenanceRecord> | null;
  /**
   * Phase 2 Session 5 — active model schema from `useModel(modelId)`.
   * Threaded through to `<MessageBubble>` for `<DeepLink>` I6 pre-validation.
   */
  modelSchema?: ModelSchema | null;
  /**
   * Phase 5 Session 2 (2026-05-08) — synthesized-turn callback. Fired by
   * `<TableFillProposal>` inside `<ToolResultRenderer>` when the user
   * clicks Confirm on an inline `propose_table_fill` proposal grid. Caller
   * resolves to `(msg) => sendMessage(activeConversationId, msg)` (same
   * pipe as a user-typed chat message) — see `routes/builder.tsx`. Required
   * because every per-tool render site can host a `propose_table_fill`
   * invocation; the callback is plumbed to all `<ToolResultRenderer>`
   * mounts unconditionally.
   */
  onSynthesizedTurn: (message: string) => void;
  /**
   * builder-ssot-consolidation Phase 5 Session 5.7 (ADR 0019 Decision B) —
   * SSOT input records from `useConversationViews().data.inputs`. Threaded
   * through to `<ToolResultRenderer>` → `<DriftBadge>` so the badge can
   * compare `result.written[k]` against the current `inputs[k].value`.
   * Optional: when absent the badge silently no-ops (no error, no badge).
   */
  inputs?: Record<string, { value: unknown }> | undefined;
  /**
   * table-input-ux (2026-06-08) — model ID threaded through to
   * `<ToolResultRenderer>` → `<TableFillProposal>` for full-grid schema
   * hydration. Optional: when absent the proposal falls back to the agent's
   * sparse columns/rows.
   */
  modelId?: string | null;
  /**
   * table-input-ux (2026-06-08) — conversation ID threaded through to
   * `<ToolResultRenderer>` → `<TableFillProposal>` for the direct write path.
   * Optional: when absent falls back to legacy synthesized-turn (D2 back-compat).
   */
  conversationId?: string | null;
  /**
   * market-default-apply-ux Phase 2 — read-only gate for the apply receipt's
   * affordances; threaded through to `<ToolResultRenderer>` →
   * `<MarketDefaultApplyReceipt>`. Optional: when absent the receipt renders
   * informationally with affordances enabled (the dispatcher's no-op handler
   * still guards the apply path).
   */
  readOnly?: boolean;
  /**
   * market-default-apply-ux Phase 2 — path-B affordance callback; threaded
   * through to `<ToolResultRenderer>` → `<MarketDefaultApplyReceipt>`. The
   * builder supplies `handleApplyMarketDefaultToInput`, which constructs the
   * `market_default_apply` interaction in-browser (ADR 0001). Optional: when
   * absent the receipt's apply affordances no-op (the dispatcher default).
   */
  onApplyToInput?: (
    field: MarketFieldValueUnit,
    aliasState: AliasMatchState
  ) => void;
}

/** Human-readable tool status messages */
const TOOL_STATUS: Record<string, string> = {
  set_inputs: 'Setting model inputs...',
  calculate: 'Running full model calculation...',
  get_values: 'Reading computed values...',
  get_dependencies: 'Tracing formula dependencies...',
  run_sensitivity: 'Running sensitivity analysis...',
  run_sensitivity_2d: 'Running 2D sensitivity matrix...',
  compare_scenarios: 'Comparing scenarios...',
  goal_seek: 'Running goal seek...',
  validate_input: 'Validating input...',
  generate_excel: 'Generating Excel workbook...',
  export_sensitivity_tab: 'Exporting sensitivity tab...',
  get_model_info: 'Querying model schema...',
  set_table_inputs: 'Setting table inputs...',
  market_data_enrichment: 'Fetching market data...',
  apply_market_defaults: 'Applying market defaults...',
  accept_defaults: 'Accepting default values...',
  propose_table_fill: 'Drafting a table proposal...',
};

/**
 * Resolve a `TableInputDefinition` from the model schema by table name,
 * case-insensitive on `name` OR `displayName` (mirrors the server's
 * `findTable` in set-table-inputs.tool.ts). Returns null when the schema
 * is absent or no table matches. (table-input-ux, 2026-06-08.)
 */
function resolveTableDef(
  modelSchema: ModelSchema | null,
  tableName: string
): TableInputDefinition | null {
  if (!modelSchema || !Array.isArray(modelSchema.tableInputs)) return null;
  const target = tableName.toLowerCase();
  const match = (modelSchema.tableInputs as TableInputDefinition[]).find(
    (t) =>
      t.name.toLowerCase() === target || t.displayName.toLowerCase() === target
  );
  return match ?? null;
}

export function ChatThread({
  messages,
  initials,
  securityBlock,
  inputProvenance = null,
  modelSchema = null,
  onSynthesizedTurn,
  inputs,
  modelId,
  conversationId,
  readOnly,
  onApplyToInput,
}: ChatThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [showStaleThinking, setShowStaleThinking] = useState(false);
  const lastContentLenRef = useRef(0);
  const lastChangeTimeRef = useRef(Date.now());

  const lastMsg = messages[messages.length - 1];
  const isLastStreaming = lastMsg?.role === 'ai' && lastMsg?.isStreaming;
  const hasActiveToolCall = !!lastMsg?.activeToolCall;

  // table-interaction-surface (2026-06-09): a `<TableEditReceipt>` "Edit" click
  // dispatches `arcanum:edit-table`, which is now handled SOLELY by the
  // composer-zone `<InteractionSurface>` (wired in routes/builder.tsx +
  // NarrativeBar.tsx). The legacy inline re-edit grid that ChatThread used to
  // spawn at the bottom of the thread was REMOVED here (brief LOCKED #1 — the
  // interaction surface REPLACES the inline grid; rendering both produced two
  // simultaneous editors). ChatThread no longer listens for the edit event.

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, lastMsg?.content]);

  // Track content freshness for stale thinking indicator (Fix #4)
  useEffect(() => {
    if (isLastStreaming && lastMsg) {
      const currentLen = lastMsg.content?.length ?? 0;
      if (currentLen !== lastContentLenRef.current) {
        lastContentLenRef.current = currentLen;
        lastChangeTimeRef.current = Date.now();
        setShowStaleThinking(false);
      }
    } else {
      setShowStaleThinking(false);
      lastContentLenRef.current = 0;
    }
  }, [lastMsg?.content, isLastStreaming]);

  // Timer to detect stale stream (no new tokens for 2s)
  useEffect(() => {
    if (!isLastStreaming || hasActiveToolCall) {
      setShowStaleThinking(false);
      return;
    }
    const interval = setInterval(() => {
      setShowStaleThinking(Date.now() - lastChangeTimeRef.current > 2000);
    }, 500);
    return () => clearInterval(interval);
  }, [isLastStreaming, hasActiveToolCall]);

  // Render-time grouping for `[model-view:*]` system messages
  // (in-platform-model-view Phase 4 Session 4). Collapses adjacent same-
  // namedRange edits within 30s into a single `<ModelViewEditGroup>` row;
  // pure function — see chat-thread-grouping.ts for the algorithm.
  // MUST run BEFORE any conditional early-return below — Rules of Hooks
  // require a stable hook order across renders, and the empty-messages
  // early return shortcuts past this if the call moves below it.
  const entries = useMemo(() => groupSystemEdits(messages), [messages]);

  // Only the LATEST `[table-edit:*]` receipt per table is editable; older
  // receipts are immutable history (no Edit affordance). Maps each table key to
  // the id of its most recent receipt — later messages overwrite, so last wins.
  // (user feedback 2026-06-09: "only the latest one should have the edit button")
  const latestTableEditIdByTable = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of messages) {
      if (m.role !== 'system') continue;
      const key = TABLE_EDIT_PREFIX_RE.exec(m.content)?.[1];
      if (key) map.set(key, m.id);
    }
    return map;
  }, [messages]);

  // A `propose_table_fill` card is "already applied" when a `[table-edit:<table>]`
  // receipt exists LATER in the thread than the proposal. This is the durable
  // applied signal for auto-apply: a re-proposal (no receipt after it yet) still
  // applies once; an old proposal never re-fires on remount (its receipt is after
  // it). Precompute message-index-by-id + the last receipt index per table.
  // (user feedback 2026-06-09 — agent re-proposal couldn't apply / be edited.)
  const { messageIndexById, lastTableEditIndexByTable } = useMemo(() => {
    const idx = new Map<string, number>();
    const lastReceipt = new Map<string, number>();
    messages.forEach((m, i) => {
      idx.set(m.id, i);
      if (m.role === 'system') {
        const key = TABLE_EDIT_PREFIX_RE.exec(m.content)?.[1];
        if (key) lastReceipt.set(key, i);
      }
    });
    return {
      messageIndexById: idx,
      lastTableEditIndexByTable: lastReceipt,
    };
  }, [messages]);

  // For each `[table-edit:*]` receipt, the snapshot of the PRIOR receipt for the
  // same table — so the receipt can enumerate what changed since the previous
  // edit. First-edit receipts have no prior (undefined → no diff shown).
  // (user feedback 2026-06-09: expandable "what changed" on receipts.)
  const previousSnapshotByReceiptId = useMemo(() => {
    const result = new Map<
      string,
      Record<string, { value: unknown; source?: string }>
    >();
    const lastByTable = new Map<
      string,
      Record<string, { value: unknown; source?: string }>
    >();
    for (const m of messages) {
      if (m.role !== 'system') continue;
      const table = TABLE_EDIT_PREFIX_RE.exec(m.content)?.[1];
      if (!table) continue;
      const prior = lastByTable.get(table);
      if (prior) result.set(m.id, prior);
      if (m.tableSnapshot) lastByTable.set(table, m.tableSnapshot);
    }
    return result;
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center overflow-y-auto">
        <div className="text-center">
          <div className="text-base text-text-muted font-body">
            No messages yet
          </div>
          <div className="text-sm text-text-dim font-mono mt-1.5">
            Start by describing your deal below
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
      {entries.map((entry) => {
        if (isModelViewEditGroup(entry)) {
          return (
            <ModelViewEditGroup
              key={`mv-group-${entry.namedRange}-${
                entry.messages[0]?.id ?? '0'
              }`}
              namedRange={entry.namedRange}
              messages={entry.messages}
            />
          );
        }
        const m: ChatMessage = entry;
        // table-input-ux (2026-06-08): a `[table-edit:*]` breadcrumb renders as
        // a `<TableEditReceipt>` — the end-of-thread confirmation of an applied
        // table edit, with an "Edit" affordance that spawns a fresh editable
        // grid at the bottom (never un-freezes the proposal card above).
        if (m.role === 'system' && TABLE_EDIT_PREFIX_RE.test(m.content)) {
          const tableKey = TABLE_EDIT_PREFIX_RE.exec(m.content)?.[1];
          const tableDef = tableKey
            ? resolveTableDef(modelSchema, tableKey)
            : null;
          const isLatest =
            tableKey != null && latestTableEditIdByTable.get(tableKey) === m.id;
          return (
            <TableEditReceipt
              key={m.id}
              content={m.content}
              conversationId={conversationId}
              tableDisplayName={tableDef?.displayName}
              tableDef={tableDef}
              inputs={inputs}
              frozenInputs={m.tableSnapshot}
              previousInputs={previousSnapshotByReceiptId.get(m.id)}
              isLatest={isLatest}
            />
          );
        }
        // System messages that AREN'T part of a multi-message group (1-of-1
        // singletons + non-model-view system messages) render as a compact
        // single-line metadata row — matches the visual weight the group
        // collapsed row uses, just without the expand affordance.
        if (m.role === 'system') {
          return (
            <div
              key={m.id}
              className="ml-8 max-w-[65%] flex items-center gap-2 px-2 py-1 text-xs font-mono text-text-muted"
              data-testid={`system-message-${m.id}`}
            >
              <span className="text-text-dim">·</span>
              <span className="truncate">
                {m.content.replace(/^\[[^\]]+\] /, '')}
              </span>
            </div>
          );
        }
        return (
          <div key={m.id} className="flex flex-col gap-2">
            {/* Mount order (Phase 2 Session 4, directive §8.2):
              1. ReasoningPill (when m.thinking !== null)
              2. PlanChecklist (when m.plan !== null)
              3. Receipt cards iterating m.toolCallStates
              4. MessageBubble
              5. SummaryCard (when m.summary !== null)
              6. Per-message security redactions (existing — unchanged) */}

            {m.role === 'ai' && m.thinking && (
              <div className="ml-8">
                <ReasoningPill
                  thinking={m.thinking}
                  isStreaming={m.isStreaming ?? false}
                />
              </div>
            )}

            {m.role === 'ai' && m.plan && (
              <div className="ml-8 max-w-[65%]">
                <PlanChecklist plan={m.plan} />
              </div>
            )}

            {/* Receipt grouping by tool (Q2/I8) — one card per tool_call_id.
              Errored phase routed to <ToolErrorCallout> by the dispatcher. */}
            {m.role === 'ai' &&
              m.toolCallStates &&
              m.toolCallStates.size > 0 && (
                <div className="flex flex-col gap-1.5 ml-8">
                  {Array.from(m.toolCallStates.entries())
                    .filter(
                      ([, state]) =>
                        !HIDDEN_FROM_RECEIPT_GROUPING.has(state.tool)
                    )
                    .map(([toolCallId, state]) => {
                      // table-input-ux (2026-06-08): resolve tableDef from
                      // modelSchema.tableInputs when this is a propose_table_fill
                      // call. The table name lives in state.input.table.
                      // `state.input` only exists on non-input_streaming phases —
                      // guard with phase check before accessing.
                      const stateInput =
                        state.phase !== 'input_streaming' ? state.input : null;
                      const tableName =
                        state.tool === 'propose_table_fill' &&
                        stateInput !== null &&
                        typeof stateInput === 'object' &&
                        typeof (stateInput as Record<string, unknown>).table ===
                          'string'
                          ? String(
                              (stateInput as Record<string, unknown>).table
                            )
                          : null;
                      const tableDef = tableName
                        ? resolveTableDef(modelSchema, tableName)
                        : null;
                      // Applied iff a `[table-edit:<table>]` receipt exists later
                      // in the thread than THIS proposal's message.
                      const proposalMsgIndex = messageIndexById.get(m.id) ?? -1;
                      const alreadyApplied =
                        tableName != null &&
                        (lastTableEditIndexByTable.get(tableName) ?? -1) >
                          proposalMsgIndex;
                      return (
                        <div key={toolCallId} className="max-w-[65%]">
                          <div className="bg-panel-bg border border-panel-border rounded-[3px] px-3.5 py-2.5">
                            <ToolResultRenderer
                              state={state}
                              onSynthesizedTurn={onSynthesizedTurn}
                              inputs={inputs}
                              modelId={modelId}
                              conversationId={conversationId}
                              tableDef={tableDef}
                              alreadyApplied={alreadyApplied}
                              readOnly={readOnly}
                              onApplyToInput={onApplyToInput}
                              runStreaming={m.isStreaming ?? false}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

            {/* Active tool call — kept as the prose-streaming pulse fallback
              (renderers handle their own executing-phase visuals via the
              dispatcher; this surface fires when no tool is active and the
              agent is producing prose tokens with no progress payloads). */}
            {m.activeToolCall && (
              <div className="ml-8 flex items-center gap-2.5 py-2">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-status" />
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-status"
                    style={{ animationDelay: '0.3s' }}
                  />
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-status"
                    style={{ animationDelay: '0.6s' }}
                  />
                </div>
                <span className="text-xs font-mono text-accent">
                  {TOOL_STATUS[m.activeToolCall] ||
                    `Running ${m.activeToolCall}...`}
                </span>
              </div>
            )}

            {m.content && (
              <MessageBubble
                role={m.role}
                content={m.content}
                initials={initials}
                dataMessageId={m.id}
                inputProvenance={inputProvenance}
                modelSchema={modelSchema}
              />
            )}

            {m.role === 'ai' && m.summary && (
              <div className="ml-8">
                <SummaryCard sections={m.summary.sections} />
              </div>
            )}

            {/* Phase 5 Session 2 — per-message security redactions (unchanged). */}
            {m.role === 'ai' &&
              m.redactedSpans &&
              m.redactedSpans.length > 0 && (
                <div className="flex flex-wrap gap-1 ml-8 max-w-[65%]">
                  {m.redactedSpans.map((rs, i) => (
                    <SecurityNotice
                      key={i}
                      variant="redact"
                      category={rs.category}
                      stage={rs.stage}
                      redactedSpan={rs.redactedSpan}
                      replacement={rs.replacement}
                    />
                  ))}
                </div>
              )}
          </div>
        );
      })}
      {/* Single thinking indicator — covers initial wait AND mid-stream pauses */}
      {isLastStreaming &&
        !hasActiveToolCall &&
        (showStaleThinking || !lastMsg?.content) && (
          <div className="ml-8 flex items-center gap-2.5 py-2">
            <div className="flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-status" />
              <span
                className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-status"
                style={{ animationDelay: '0.3s' }}
              />
              <span
                className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-status"
                style={{ animationDelay: '0.6s' }}
              />
            </div>
            <span className="text-xs font-mono text-text-muted">
              {!lastMsg?.content ? 'Analyzing your request...' : 'Thinking...'}
            </span>
          </div>
        )}

      {/* Phase 5 Session 2 — terminating security block notice. Rendered at the
          bottom of the chat thread when the API SSE controller pre-empted on
          a security_block event (input filter verdict='block'). The user's
          message was never delivered to the agent; this banner is the only
          response. */}
      {securityBlock && (
        <div className="ml-8 max-w-[65%]">
          <SecurityNotice variant="block" reason={securityBlock.reason} />
        </div>
      )}

      {/* table-interaction-surface (2026-06-09): the inline re-edit grid that
          used to spawn here on "Edit" was removed — the composer-zone
          `<InteractionSurface>` (routes/builder.tsx + NarrativeBar.tsx) is now
          the single edit surface (brief LOCKED #1). */}

      <div ref={bottomRef} />
    </div>
  );
}
